import { getToken } from "./identity";
import { ApiError } from "./apiClient";
import { withRetry } from "@shared/retry";

/**
 * Thrown only when the stream ends (reader reports done) without ever
 * delivering a data: frame — genuinely ambiguous from here whether the
 * network connection dropped or the server-side synthesis died without
 * emitting its own in-band error frame, but either way nothing useful
 * arrived and retrying the whole request is the only recourse. Kept
 * distinct from the plain ApiError a real in-band `data: {"error":...}`
 * frame throws (line ~64 below) — that one is a genuine, already-reported
 * synthesis failure and must not be retried, only this ambiguous
 * stream-ended-early case should be.
 */
export class StreamEndedWithoutDataError extends ApiError {
  constructor() {
    super(502, "The connection was lost before a response arrived. Please try again.");
  }
}

/** Exported for its own test — see tests/askStreamRetry.test.ts. */
export function isAskStreamRetryable(err: unknown): boolean {
  return err instanceof StreamEndedWithoutDataError || err instanceof TypeError;
}

/** One attempt — no retry logic here, see postAskStream below. */
async function postAskStreamOnce<T>(path: string, body: unknown): Promise<T> {
  const token = await getToken();
  const headers = new Headers();
  headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(path, { method: "POST", headers, body: JSON.stringify(body) });

  // Pre-stream failures (auth/validation/rate-limit/404) are unchanged --
  // still one normal buffered JSON error body, exactly like api.post today.
  if (!res.ok) {
    const text = await res.text();
    let message = `Request failed (${res.status})`;
    try {
      const data = text ? JSON.parse(text) : {};
      if (data.error) message = data.error;
    } catch {
      // keep the generic message
    }
    throw new ApiError(res.status, message);
  }
  if (!res.body) throw new StreamEndedWithoutDataError();

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let boundary: number;
      while ((boundary = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, boundary).trim();
        buffer = buffer.slice(boundary + 2);
        if (frame.length === 0 || frame.startsWith(":")) continue; // heartbeat/comment
        if (!frame.startsWith("data:")) continue;
        let payload: unknown;
        try {
          payload = JSON.parse(frame.slice("data:".length).trim());
        } catch {
          throw new Error("The answer service returned an unreadable response.");
        }
        if (payload && typeof payload === "object" && "error" in payload) {
          throw new ApiError(502, String((payload as { error: unknown }).error));
        }
        return payload as T;
      }
    }
  } finally {
    reader.releaseLock();
  }
  throw new StreamEndedWithoutDataError();
}

/**
 * POST to /ask specifically. The server (netlify/functions/projects-ask.ts)
 * returns a live text/event-stream body: periodic ": keep-alive\n\n"
 * comment frames while synthesis runs (keeps the connection alive past any
 * idle timeout a slow model would otherwise hit), followed by exactly one
 * "data: {...}\n\n" frame carrying the complete answer — or, if synthesis
 * failed after the stream already opened, "data: {\"error\":...}\n\n"
 * (headers are already committed once streaming starts, so an in-band error
 * frame is the only way to signal that, rather than a real HTTP status).
 * Resolves with the same complete object api.post<T> would have returned —
 * callers don't need to know this is streamed under the hood.
 *
 * Retries on a stream that ended without ever delivering a data frame, or a
 * bare network-level fetch failure — reproduced live in production (see
 * issue tracker): a real question failed twice in a row with "The
 * connection was lost before a response arrived," with no retry recourse
 * for the user beyond manually resubmitting. This is the same failure mode
 * scripts/run-eval.ts's own /ask caller already had and was fixed for
 * (withRetry, shared/retry.ts) — that fix never reached this, the actual
 * path a field user hits.
 */
export async function postAskStream<T>(path: string, body: unknown): Promise<T> {
  return withRetry(() => postAskStreamOnce<T>(path, body), {
    attempts: 3,
    baseDelayMs: 500,
    isRetryable: isAskStreamRetryable,
  });
}
